import React, { useState, useEffect } from 'react';
import { DataGrid } from '@mui/x-data-grid';
import ApiService from '../../../services/ApiService';
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  Grid,
  MenuItem,
  Modal,
  Snackbar,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import Menu from '@mui/material/Menu';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ExcelIcon from '../../../components/Icon/ExcelIcon';
import { LoadingButton } from '@mui/lab';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import FileUpload from '../Create/FileUpload';
import XLSX from 'xlsx';

const DataTableCreator = ({
  taskListAuthorization,
  stateRegion,
  stateLokasi,
  stateKegiatan,
  download,
  isDownloading,
}) => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [regionId, setRegionId] = useState(0);
  const [dataRegion, setDataRegion] = stateRegion;
  const [dataLokasi, setDataLokasi] = stateLokasi;
  const [dataKegiatan, setDataKegiatan] = stateKegiatan;
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [isLoadingActionMenu, setIsLoadingActionMenu] = useState(false);
  const [open, setOpen] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');

  const history = useNavigate();

  const [pagination, setPagination] = useState({
    totalCount: 0,
    pageSize: 5,
    currentPage: 1,
    totalPages: 0,
    previousPage: false,
    nextPage: false,
    payload: {
      regionId: 0,
      locationId: 0,
      kegiatan: 0,
      role: 'creator',
    },
  });

  const fetchData = async () => {
    setIsLoading(true);
    ApiService.getActionTrackingTaskListCreator({
      page: pagination.currentPage,
      itemPerPage: pagination.pageSize,
      regionId: pagination.payload.regionId,
      locationId: pagination.payload.locationId,
      kegiatan: pagination.payload.kegiatan,
      role: pagination.payload.role,
      emailLogin: '',
    })
      .then(({ data }) => {
        setData(data.payload.data);
        setPagination({ ...pagination, ...data.payload.pagination });
      })
      .catch((error) => {
        console.error('Error fetching data:', error);
        setData([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, [
    pagination.currentPage,
    pagination.pageSize,
    pagination.payload.regionId,
    pagination.payload.locationId,
    pagination.payload.kegiatan,
    pagination.payload.role,
  ]);

  const handlePageChange = (page) => {
    setPagination((prevPagination) => ({
      ...prevPagination,
      currentPage: page + 1,
    }));
  };

  const onChangeRegion = (event, value) => {
    if (!value) {
      setRegionId(0);
      setDataLokasi([]);
      setPagination((prevPagination) => ({
        ...prevPagination,
        payload: {
          ...prevPagination.payload,
          regionId: 0,
        },
      }));
      return;
    }
    setPagination((prevPagination) => ({
      ...prevPagination,
      payload: {
        ...prevPagination.payload,
        regionId: value.id,
      },
    }));
    setRegionId(value.id);
  };

  const getDataLokasi = () => {
    if (!regionId) return;
    setIsLoading(true);
    ApiService.getMsLokasi(regionId)
      .then((res) => {
        var result = res.data.payload;
        setDataLokasi(result);
      })
      .catch(() => {
        setDataLokasi([]);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getDataLokasi();
  }, [regionId]);

  const handleButtonClick = (event, params) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowId(params.row.paramActionTrackingEncode);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowId(null);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleMenuItemClick = (event, action, row) => {
    setIsLoadingActionMenu(true);
    ApiService.getActionTrackingById(row.paramActionTrackingEncode)
      .then(({ data }) => {
        if (
          data &&
          data.payload &&
          data.payload.statusHeaderName !== 'Completed' &&
          (data.payload.role === 'Admin' || data.payload.role === 'Creator') &&
          (data.payload.statusHeaderName === 'Draft' || 'Open')
        ) {
          history(`/hsse/action-tracking/${selectedRowId}`, {
            state: { isEdit: true },
          });
        } else {
          setSnackbarMessage(data.message);
          setSnackbarOpen(true);
          return;
        }
      })
      .finally(() => {
        setIsLoadingActionMenu(false);
      });
    handleMenuClose();
  };

  const getMenuItems = (row) => {
    const availableActions =
      row.statusName === 'Completed' ? ['VIEW'] : ['EDIT'];

    return availableActions.map((action) => (
      <MenuItem
        key={action}
        onClick={(event) => handleMenuItemClick(event, action, row)}
      >
        {action}
      </MenuItem>
    ));
  };

  const BulkInsertModal = () => {
    const modalStyle = {
      position: 'absolute',
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      width: '40%',
      margin: '2% 0',
      bgcolor: 'background.paper',
      border: '2px solid #000',
      boxShadow: 24,
      p: 3,
    };

    const handleClose = () => {
      setOpen(false);
    };

    const handleBulkInsert = ({ countSuccess, countFailed, errorMessage }) => {
      if (countFailed > 0) {
        const wb = XLSX.utils.book_new();
        const excelData = errorMessage.map((error) => ({ error }));
        const ws = XLSX.utils.json_to_sheet(excelData);

        const rowsToAdd = [
          ['Count Failed', countFailed],
          ['Count Success', countSuccess],
          [''],
        ];

        XLSX.utils.sheet_add_aoa(ws, rowsToAdd, { origin: -1 });
        XLSX.utils.book_append_sheet(wb, ws, 'errors');

        const dateTimeString = new Date().toISOString().replace(/:/g, '-');
        const fileName = `error_bulk_insert_${dateTimeString}.xlsx`;
        XLSX.writeFile(wb, fileName);
      }

      errorMessage.length > 0
        ? setSnackbarMessage('Bulk insert has errors')
        : setSnackbarMessage('Bulk insert success');

      fetchData().finally(() => {
        setOpen(false);
        setSnackbarOpen(true);
      });
    };

    return (
      <Modal open={open} onClose={handleClose}>
        <Box component="div" sx={modalStyle}>
          <Grid container spacing={2} marginBottom={3}>
            <Grid item xs={12}>
              <Typography variant="h6" component="h2">
                Bulk Insert
              </Typography>
            </Grid>
            <Grid item xs={12} sx={{ marginTop: 2 }}>
              <FileUpload
                customApi={ApiService.bulkInsert}
                onFileUploaded={handleBulkInsert}
                fileFormat={[
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                  'application/vnd.ms-excel',
                ]}
                onFileDeleted={() => {}}
                fileExtension={['.xls', '.xlsx']}
              />
            </Grid>
          </Grid>
        </Box>
      </Modal>
    );
  };

  return (
    <Grid item xs={12} marginTop={3}>
      <Grid
        container
        spacing={2}
        marginBottom={3}
        justifyContent="space-around"
        alignItems="center"
      >
        <Grid
          item
          xs={10}
          container
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
        >
          {taskListAuthorization.aC_CREATE_ACTION_TRACKING && (
            <Grid
              item
              xs={3}
              container
              justifyContent="space-between"
              alignItems="center"
              spacing={2}
            >
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  sx={{
                    width: '100%',
                    height: '56px',
                    whiteSpace: 'normal',
                    textTransform: 'none',
                  }}
                  onClick={() => history('/hsse/action-tracking/create')}
                >
                  New
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<ExcelIcon />}
                  sx={{
                    width: '100%',
                    height: '56px',
                    whiteSpace: 'normal',
                    textTransform: 'none',
                  }}
                  onClick={() => setOpen(true)}
                >
                  Bulk
                </Button>
              </Grid>
            </Grid>
          )}
          <Grid item xs={3}>
            <FormControl fullWidth>
              <Autocomplete
                name="idRegional"
                onChange={onChangeRegion}
                required
                getOptionLabel={(option) => option.name}
                options={dataRegion}
                renderInput={(params) => (
                  <TextField {...params} label="Regional" />
                )}
                getOptionSelected={(option, value) =>
                  option.name === value.name
                }
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <Autocomplete
                name="idLokasi"
                onChange={(e, value) => {
                  setPagination({
                    ...pagination,
                    payload: {
                      ...pagination.payload,
                      locationId: value ? value.id : 0,
                    },
                  });
                }}
                required
                getOptionLabel={(option) => option.name}
                options={dataLokasi}
                renderInput={(params) => (
                  <TextField {...params} label="Lokasi" />
                )}
                getOptionSelected={(option, value) =>
                  option.name === value.name
                }
                loading={isLoading}
              />
            </FormControl>
          </Grid>
          <Grid item xs={3}>
            <FormControl fullWidth>
              <Autocomplete
                name="kegiatan"
                onChange={(e, value) => {
                  setPagination({
                    ...pagination,
                    payload: {
                      ...pagination.payload,
                      kegiatan: value ? value.id : 0,
                    },
                  });
                }}
                required
                getOptionLabel={(option) => option.name}
                options={dataKegiatan}
                renderInput={(params) => (
                  <TextField {...params} label="Kegiatan" />
                )}
                getOptionSelected={(option, value) =>
                  option.name === value.name
                }
              />
            </FormControl>
          </Grid>
          {!taskListAuthorization.aC_CREATE_ACTION_TRACKING && (
            <Grid item xs={3}></Grid>
          )}
        </Grid>
        <Grid item xs={2} justifyContent="center" alignItems="center" container>
          {taskListAuthorization.aC_EXPORT_DATA && (
            <LoadingButton
              variant="text"
              color="primary"
              startIcon={<ExcelIcon />}
              loading={isDownloading}
              onClick={() =>
                download({ ...pagination.payload, role: 'creator' })
              }
            >
              Download
            </LoadingButton>
          )}
        </Grid>
      </Grid>

      <Grid container spacing={2} marginBottom={3}>
        <Grid item xs={12}>
          <DataGrid
            autoHeight={true}
            sx={{ width: '100%' }}
            rows={data}
            disableColumnFilter={true}
            disableColumnSelector={true}
            disableSelectionOnClick={true}
            disableColumnMenu={true}
            columns={[
              {
                field: 'action',
                headerName: 'Action',
                sortable: false,
                renderCell: (params) => (
                  <>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={(event) => handleButtonClick(event, params)}
                      disabled={isLoadingActionMenu}
                    >
                      Action
                    </Button>
                    <Menu
                      anchorEl={anchorEl}
                      open={
                        Boolean(anchorEl) &&
                        selectedRowId === params.row.paramActionTrackingEncode
                      }
                      onClose={handleMenuClose}
                    >
                      {getMenuItems(params.row)}
                    </Menu>
                  </>
                ),
              },
              {
                field: 'statusName',
                headerName: 'Status',
                sortable: false,
                renderCell: (params) => {
                  let cellValue = params.value;
                  let cellColor = '';

                  switch (cellValue) {
                    case 'Open':
                      cellColor = '#ff9100'; // Orange
                      break;
                    case 'On Progress':
                      cellColor = '#FFFF00'; // Light Blue
                      break;
                    case 'In Progress':
                      cellColor = '#ffeb3b'; // Light Blue
                      break;
                    case 'Closed':
                      cellColor = '#98FB98';
                    case 'Completed':
                      cellColor = '#333333'; // Dark Gray
                      break;
                    default:
                      cellColor = '#000000'; // Black
                      break;
                  }

                  return (
                    <div
                      style={{
                        borderRadius: '5px',
                        border: '#adada8 2px solid',
                        padding: '8px 2px',
                        width: '120px',
                        alignItems: 'center',
                        textAlign: 'center',
                      }}
                    >
                      <span
                        style={{
                          color: cellColor,
                          fontWeight: 'bold',
                        }}
                      >
                        {cellValue}
                      </span>
                    </div>
                  );
                },
              },
              {
                field: 'idStatus',
                headerName: 'Status ID',
                hide: true,
              },
              {
                field: 'nomorActionTracking',
                headerName: 'No. Action Tracking',
                width: 300,
                sortable: false,
              },
              {
                field: 'judul',
                headerName: 'Judul',
                width: 340,
                sortable: false,
                renderCell: (params) => (
                  <Tooltip title={params.value}>
                    <span>
                      {params.value && params.value.length > 25
                        ? `${params.value.substring(0, 40)}...`
                        : params.value}
                    </span>
                  </Tooltip>
                ),
              },
              {
                field: 'idKegiatan',
                headerName: 'Kegiatan',
                hide: true,
              },
              {
                field: 'namaKegiatan',
                headerName: 'Kegiatan',
                width: 240,
                sortable: false,
              },
              {
                field: 'idRegional',
                headerName: 'Regional ID',
                hide: true,
              },
              {
                field: 'namaRegional',
                headerName: 'Regional',
                sortable: false,
                width: 240,
              },
              {
                field: 'idLokasi',
                headerName: 'Location ID',
                hide: true,
                width: 240,
              },
              {
                field: 'namaLokasi',
                headerName: 'Location',
                sortable: false,
                width: 240,
              },
              { field: 'createdDate', headerName: 'Created Date', hide: true },
              {
                field: 'paramActionTrackingEncode',
                hide: true,
                headerName: 'Encoded',
              },
            ]}
            pagination
            rowCount={pagination.totalCount}
            page={pagination.currentPage - 1}
            onPageChange={handlePageChange}
            paginationMode="server"
            getRowId={(row) => row.paramActionTrackingEncode}
            pageSize={pagination.pageSize}
            onPageSizeChange={(newPageSize) =>
              setPagination({
                ...pagination,
                pageSize: newPageSize,
                currentPage: 1,
              })
            }
            rowsPerPageOptions={[1, 5, 10, 20, 50]} // Customize available options
            loading={isLoading}
          />
        </Grid>
      </Grid>
      <BulkInsertModal />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
      />
    </Grid>
  );
};

export default DataTableCreator;
